import { ViewerAssetJsonType, ViewerAssetType, ViewerLanguage, ViewerEdition } from '@shared/enums/viewer-asset';

import { ClassCast } from '@shared/zb-object-helper/class-cast';
import { IProductComponent } from './product-component';

export class NavigationToggle {
  previous: string = undefined;
  next: string = undefined;
}

export class PageInfo {
  page: number;
  pages: number;
}

// Legacy file-based.
export interface IViewerAssetJson {
  category: string;
  id: string;
  additionalInfo: string;
  type: ViewerAssetJsonType;
  name: string;
  filename: string;
  engine: string;
  language: ViewerLanguage;
  weight?: number;
  edition?: ViewerEdition;
  toggle?: string[];
  thumbnail?: string;
}

// Api-based.
export interface IViewerAsset {
  assetId: string;
  additionalInfo: string;
  componentId: string;
  category: string;
  edition?: ViewerEdition;
  engine?: string;
  weight: number;
  name: string;
  fileName: string;
  viewerAssetType: ViewerAssetType;
  language: ViewerLanguage;
  instructions: string;
  tags: string[];
  toggles: string[];
  relatedAudio?: string[];
  relatedAudioIds?: string[];
  visible?: boolean;
  navigationToggles?: NavigationToggle;
  thumbnail: string;
  alternateText?: string;
}

export class ViewerAsset implements IViewerAsset {
  assetId: string;
  additionalInfo: string;
  componentId: string;
  category: string;
  weight: number;
  name: string;
  edition?: ViewerEdition;
  engine?: string;
  fileName: string;
  viewerAssetType: ViewerAssetType;
  language: ViewerLanguage;
  instructions: string;
  transcript?: string;  // New property for audio transcripts
  tags: string[];
  relatedAudio?: string[];
  relatedAudioIds?: string[];
  toggles: string[];
  visible?: boolean;
  linkToAsset?: string;
  alternateText?: string;
  @ClassCast(NavigationToggle)
    navigationToggles?: NavigationToggle = undefined;
  pageInfo?: PageInfo;
  thumbnail: string;
  linkToThumbnail?: string;
  hasPrintableAssets?: boolean;

  constructor(values?: IViewerAsset) {
    if (values) {
      Object.keys(values).forEach((key: string) => {
        if (Object.prototype.hasOwnProperty.call(values, key) && values[key]) {
          this[key] = values[key];
        }
      });
    }
  }

  /**
   * Creates viewer asset object from legacy JSON structure.
   */
  static fromJson(componentId: string, values: IViewerAssetJson): ViewerAsset {
    let viewerAssetType: ViewerAssetType = null;
    if (values.type === ViewerAssetJsonType.canvas) {
      viewerAssetType = ViewerAssetType.Canvas;
    } else if (values.type === ViewerAssetJsonType.image) {
      viewerAssetType = ViewerAssetType.Image;
    } else if (values.type === ViewerAssetJsonType.pdf) {
      viewerAssetType = ViewerAssetType.Pdf;
    } else if (values.type === ViewerAssetJsonType.video) {
      viewerAssetType = ViewerAssetType.Video;
    } else if (values.type === ViewerAssetJsonType.video_canvas) {
      viewerAssetType = ViewerAssetType.VideoCanvas;
    } else {
      // ImagePrint, PdfLocked, Iwb, Dr, Audio, SuperSmart, Set are all Superkids asset types that were introduced
      // after the specification for viewer asset spreadsheets was defined for supplemental products.
      throw new Error('Asset type is not supported for this product.');
    }

    return new ViewerAsset({
      assetId: values.id,
      additionalInfo: values.additionalInfo,
      name: values.name,
      componentId,
      viewerAssetType,
      language: values.language || ViewerLanguage.en,
      edition: values.edition,
      fileName: values.filename,
      toggles: values.toggle || [],
      engine: values.engine || null,
      relatedAudio: [],
      category: values.category,
      tags: [],
      instructions: null,
      weight: values.weight || 0,
      visible: true,
      thumbnail: values.thumbnail,
      alternateText: '',
    });
  }
}

export interface IProductComponentViewerAssets extends IProductComponent {
  viewerAssets: IViewerAsset[];
}
